// Dispatcher home — CNC-parity morning workload planner.
//
// Layout mirrors CNC's /dispatch view: workweek stepper across the top,
// "{Day} Delivery" headline with Send Routes action (Upload orders is
// a pharmacy-side action, not surfaced here — retracted 2026-07-10),
// big-number stat trio (Total Stops / Cold Chain / Active Drivers),
// pharmacy cohorts as sections with a blue count badge, and a footer
// listing drivers with no work today.
//
// Visual deferrals (not in this slice): pharmacies.short_code +
// brand_color columns (today: short_code is derived from name's first
// word, color is a stable hash of pharmacy_id); drivers.driver_number
// chip (LYN Rx schema has no equivalent — hidden); orders.package_count
// "(N PKG)" qualifier (no schema column today — stops count only).

import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as Sentry from '@sentry/react';
import type { LynrxSupabaseClient } from '@lynrx/shared';
import { useAuth } from '../lib/auth/auth-context';
import { requireUserType } from '../lib/auth/guards';
import { supabase as defaultClient } from '../lib/supabase';
import { Alert } from '../components/ui/Alert';
import { Card } from '../components/ui/Card';
import { StatusBadge } from '../components/ui/StatusBadge';
import { useSetPageHeader } from '../components/layout/PageHeaderContext';
import { DispatchMap, type DispatchMapStop } from '../components/dispatch/DispatchMap';

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

type DispatchSearch = { date: string };

export const Route = createFileRoute('/_authenticated/dispatch')({
  // Route-level role gate (defense in depth — the Sidebar already
  // hides /dispatch from non-dispatchers, but typing the URL by hand
  // shouldn't reach the page either). `platform_admin` is allowed via
  // the LYN-staff override inside requireUserType so the role can be
  // previewed during build-out. RLS remains the safety net under the
  // page's queries.
  beforeLoad: async () => {
    await requireUserType({ allowed: ['dispatcher'] });
  },
  validateSearch: (search: Record<string, unknown>): DispatchSearch => {
    const raw = search['date'];
    const date = typeof raw === 'string' && DATE_RE.test(raw) ? raw : todayIso();
    return { date };
  },
  component: DispatchPage,
});

function DispatchPage(): JSX.Element {
  // For dispatchers, /dispatch IS their dashboard — the sidebar nav
  // labels it "Dashboard" too. The page's own day stepper + "Monday
  // Delivery" headline carries the working context; the shell's top
  // bar is suppressed so the page label doesn't render twice.
  useSetPageHeader({ title: 'Dispatch', hideTopBar: true });
  const { date } = Route.useSearch();
  const navigate = useNavigate();
  const setDate = useCallback(
    (next: string) => {
      void navigate({ to: '/dispatch', search: { date: next }, replace: true });
    },
    [navigate],
  );
  return <DispatchView date={date} onDateChange={setDate} />;
}

// ----------------------------------------------------------------------------
// Date helpers — workweek-of-date math, all in local-calendar terms so
// negative UTC offsets don't tip the day backward.

function todayIso(): string {
  const d = new Date();
  return toIso(d);
}

function toIso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseIso(iso: string): Date {
  // Build a local-calendar Date (NOT UTC) — matches /routes/index.
  const parts = iso.split('-');
  const y = Number(parts[0]);
  const m = Number(parts[1]);
  const d = Number(parts[2]);
  return new Date(y, m - 1, d);
}

// Monday-anchored workweek (Mon..Fri) containing the given date.
export function workweekOf(iso: string): string[] {
  const d = parseIso(iso);
  // getDay(): Sun=0, Mon=1, ..., Sat=6. Move back to the Monday of
  // this week. If today is Sun, the next-Mon convention (CNC behavior)
  // would push to tomorrow — but Sun is a rare dispatch day, so we use
  // the previous Mon. Dispatchers can step forward with the stepper.
  const dow = d.getDay();
  const offsetToMonday = dow === 0 ? -6 : 1 - dow;
  const monday = new Date(d.getFullYear(), d.getMonth(), d.getDate() + offsetToMonday);
  return Array.from({ length: 5 }, (_, i) => {
    const day = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + i);
    return toIso(day);
  });
}

function shortDayLabel(iso: string): string {
  return parseIso(iso).toLocaleDateString(undefined, { weekday: 'short' });
}

function longDayLabel(iso: string): string {
  return parseIso(iso).toLocaleDateString(undefined, { weekday: 'long' });
}

function rangeLabel(start: string, end: string): string {
  const s = parseIso(start);
  const e = parseIso(end);
  const sameMonth = s.getMonth() === e.getMonth();
  const sFmt = s.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const eFmt = sameMonth
    ? e.toLocaleDateString(undefined, { day: 'numeric' })
    : e.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return `${sFmt} – ${eFmt}`;
}

function fullDateLabel(iso: string): string {
  return parseIso(iso).toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

// ----------------------------------------------------------------------------
// Data shape + grouping reducer. Pure — exercised by
// _authenticated.dispatch.test.tsx with no Supabase stub required.

type DispatchRouteRow = {
  id: string;
  route_status: string;
  driver_id: string | null;
  driver: { full_name: string | null; email: string } | null;
  pharmacy: { id: string; name: string } | null;
  stops: {
    id: string;
    // lat/lng/address arrive from PostgREST — numerics come back as
    // strings. Only needed for the Map view; the Stops view ignores
    // them. Kept nullable because unmapped stops (missing coords)
    // are dropped downstream by the map's parseStops helper.
    latitude: string | number | null;
    longitude: string | number | null;
    address: string | null;
    orders: { id: string; status: string; is_cold_chain: boolean }[];
  }[];
};

type DriverCard = {
  key: string; // routeId — one card per route, not per driver
  driverId: string | null;
  driverLabel: string;
  routeId: string;
  routeStatus: string;
  stopCount: number;
  // Number of dropoff orders on this route in the `delivered` status.
  // Drives the inline progress bar.
  deliveredCount: number;
  coldChainCount: number;
};

type PharmacyGroup = {
  pharmacyId: string;
  pharmacyName: string;
  shortCode: string;
  colorToken: PharmacyColor;
  drivers: DriverCard[];
};

type Totals = {
  totalStops: number;
  coldChain: number;
  activeDriverIds: Set<string>;
};

// Statuses that contribute to "today's workload" totals. Cancelled
// routes don't represent active dispatch work; everything else does
// (drafts are pre-publish workload, published/in-progress/completed
// are live or finished).
const ACTIVE_STATUSES = [
  'draft',
  'assigned',
  'in_progress',
  'completed',
] satisfies ReadonlyArray<
  'draft' | 'assigned' | 'in_progress' | 'completed' | 'cancelled' | 'published'
>;

// Stable color tokens for the pharmacy chip — CNC has a `brand_color`
// column on its pharmacy table; LYN Rx doesn't yet. Until that lands
// we hash the pharmacy_id to one of these five so each pharmacy gets a
// consistent visual identity across refreshes. The Tailwind class map
// lives below; this type is the bridge.
export type PharmacyColor = 'emerald' | 'sky' | 'amber' | 'violet' | 'slate';
const COLOR_TOKENS: PharmacyColor[] = ['emerald', 'sky', 'amber', 'violet', 'slate'];

export function hashPharmacyColor(pharmacyId: string): PharmacyColor {
  // Simple deterministic hash. Sum of char codes mod palette size.
  let n = 0;
  for (let i = 0; i < pharmacyId.length; i++) n = (n + pharmacyId.charCodeAt(i)) | 0;
  return COLOR_TOKENS[Math.abs(n) % COLOR_TOKENS.length] ?? 'slate';
}

export function shortCodeFromName(name: string): string {
  // First token of the pharmacy name uppercased, truncated to 8 chars.
  // "Aultman" → "AULTMAN", "Summa Health" → "SUMMA", "Demo Pharmacy" → "DEMO".
  const first = name.trim().split(/\s+/)[0] ?? name;
  return first.toUpperCase().slice(0, 8);
}

function driverDisplay(row: DispatchRouteRow): string {
  if (row.driver?.full_name) return row.driver.full_name;
  if (row.driver?.email) return row.driver.email;
  return 'Unassigned';
}

function stopCount(row: DispatchRouteRow): number {
  return row.stops.length;
}

function coldChainCount(row: DispatchRouteRow): number {
  let n = 0;
  for (const stop of row.stops) {
    for (const order of stop.orders) {
      if (order.is_cold_chain) n += 1;
    }
  }
  return n;
}

function deliveredCount(row: DispatchRouteRow): number {
  let n = 0;
  for (const stop of row.stops) {
    for (const order of stop.orders) {
      if (order.status === 'delivered') n += 1;
    }
  }
  return n;
}

// Flattens all stops from all routes into the shape DispatchMap
// consumes. Each stop is decorated with its parent route's driver
// context, and stop-level `is_cold_chain` is derived from ANY of the
// stop's dropoff orders being cold-chain. Pure — covered by the
// dispatch test suite alongside groupRoutes.
export function flattenStopsForMap(rows: DispatchRouteRow[]): DispatchMapStop[] {
  const out: DispatchMapStop[] = [];
  for (const r of rows) {
    const name = r.driver?.full_name ?? r.driver?.email ?? 'Unassigned';
    for (const s of r.stops) {
      out.push({
        id: s.id,
        latitude: s.latitude,
        longitude: s.longitude,
        address: s.address,
        driver_id: r.driver_id,
        driver_name: name,
        is_cold_chain: (s.orders ?? []).some((o) => o.is_cold_chain),
      });
    }
  }
  return out;
}

export function groupRoutes(rows: DispatchRouteRow[]): {
  groups: PharmacyGroup[];
  totals: Totals;
} {
  const groups = new Map<string, PharmacyGroup>();
  const activeDriverIds = new Set<string>();
  let totalStops = 0;
  let coldChain = 0;

  for (const row of rows) {
    if (!row.pharmacy) continue; // RLS shouldn't surface these, but be defensive
    const stops = stopCount(row);
    const cold = coldChainCount(row);
    totalStops += stops;
    coldChain += cold;
    if (row.driver_id) activeDriverIds.add(row.driver_id);

    const group =
      groups.get(row.pharmacy.id) ?? {
        pharmacyId: row.pharmacy.id,
        pharmacyName: row.pharmacy.name,
        shortCode: shortCodeFromName(row.pharmacy.name),
        colorToken: hashPharmacyColor(row.pharmacy.id),
        drivers: [],
      };
    group.drivers.push({
      key: row.id,
      driverId: row.driver_id,
      driverLabel: driverDisplay(row),
      routeId: row.id,
      routeStatus: row.route_status,
      stopCount: stops,
      deliveredCount: deliveredCount(row),
      coldChainCount: cold,
    });
    groups.set(row.pharmacy.id, group);
  }

  const ordered = Array.from(groups.values()).sort((a, b) =>
    a.pharmacyName.localeCompare(b.pharmacyName),
  );
  for (const g of ordered) {
    g.drivers.sort((a, b) => {
      if (a.driverId === null && b.driverId !== null) return 1;
      if (a.driverId !== null && b.driverId === null) return -1;
      return a.driverLabel.localeCompare(b.driverLabel);
    });
  }

  return {
    groups: ordered,
    totals: { totalStops, coldChain, activeDriverIds },
  };
}

// ----------------------------------------------------------------------------
// View component.

type DriverRow = {
  id: string;
  user: { full_name: string | null; email: string } | null;
};

type ViewProps = {
  date: string;
  onDateChange: (next: string) => void;
  // Test seam — defaults to the singleton supabase client.
  client?: LynrxSupabaseClient;
};

type SendRoutesFeedback = {
  tone: 'success' | 'error';
  text: string;
};

export function DispatchView({
  date,
  onDateChange,
  client = defaultClient,
}: ViewProps): JSX.Element {
  const { session, isReady } = useAuth();
  const [rows, setRows] = useState<DispatchRouteRow[] | null>(null);
  const [allDrivers, setAllDrivers] = useState<DriverRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [sending, setSending] = useState(false);
  const [feedback, setFeedback] = useState<SendRoutesFeedback | null>(null);
  // Segmented view — Stops (default, existing cohort grid) or Map
  // (Phase 1 CNC-parity dispatch map). Local state; not persisted.
  const [view, setView] = useState<'stops' | 'map'>('stops');

  // Routes for the visible day.
  const fetchDay = useCallback(
    async (d: string) => {
      if (!session) return;
      setError(null);
      const { data, error: err } = await client
        .from('routes')
        .select(
          'id, route_status, driver_id, driver:users!routes_driver_id_fkey(full_name, email), pharmacy:pharmacies!routes_pharmacy_id_fkey(id, name), stops(id, latitude, longitude, address, orders!orders_dropoff_stop_id_fkey(id, status, is_cold_chain))',
        )
        .is('deleted_at', null)
        .eq('planned_for', d)
        .in('route_status', ACTIVE_STATUSES);
      if (err) {
        Sentry.captureException(err, { tags: { area: 'dispatch.fetch' } });
        setError('Could not load today’s workload.');
        return;
      }
      setRows((data ?? []) as unknown as DispatchRouteRow[]);
    },
    [client, session],
  );

  // All tenant drivers — feeds the "Active drivers n/m" denominator and
  // the "NO STOPS TODAY" footer. RLS already scopes this to drivers the
  // caller can see (dispatcher = tenant_wide).
  const fetchDrivers = useCallback(async () => {
    if (!session) return;
    const { data, error: err } = await client
      .from('drivers')
      .select('id, user:users!drivers_id_fkey(full_name, email)')
      .is('deleted_at', null);
    if (err) {
      Sentry.captureException(err, { tags: { area: 'dispatch.fetch_drivers' } });
      setAllDrivers([]);
      return;
    }
    setAllDrivers((data ?? []) as unknown as DriverRow[]);
  }, [client, session]);

  useEffect(() => {
    if (!isReady || !session) return;
    void fetchDay(date);
    // Clear selection + feedback on date change — they are scoped to the
    // current day's drafts.
    setSelected(new Set());
    setFeedback(null);
  }, [isReady, session, fetchDay, date]);

  useEffect(() => {
    if (!isReady || !session) return;
    void fetchDrivers();
  }, [isReady, session, fetchDrivers]);

  const { groups, totals } = useMemo(() => {
    if (!rows) {
      return {
        groups: [] as PharmacyGroup[],
        totals: { totalStops: 0, coldChain: 0, activeDriverIds: new Set<string>() },
      };
    }
    return groupRoutes(rows);
  }, [rows]);

  // Flattened stops for the Map view. Memoized so the map doesn't
  // rebuild on unrelated state changes (selection, feedback banner).
  const mapStops = useMemo<DispatchMapStop[]>(
    () => (rows ? flattenStopsForMap(rows) : []),
    [rows],
  );

  // Drivers with no route on this date — used for the footer band.
  const noStopsDrivers = useMemo(() => {
    if (!allDrivers || !rows) return [];
    const driversWithWork = new Set<string>();
    for (const r of rows) {
      if (r.driver_id) driversWithWork.add(r.driver_id);
    }
    return allDrivers
      .filter((d) => !driversWithWork.has(d.id))
      .map((d) => ({
        id: d.id,
        label: d.user?.full_name ?? d.user?.email ?? d.id.slice(0, 8),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allDrivers, rows]);

  // Selection toggle — drafts only. Other statuses can't be re-published.
  const toggleSelected = useCallback((routeId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(routeId)) next.delete(routeId);
      else next.add(routeId);
      return next;
    });
  }, []);

  // Send Routes — fires publish_route for each selected draft route in
  // parallel. Optimistically removes successfully-published ids from the
  // selection set; refetches the day on completion so cards re-render
  // with the new status.
  const sendRoutes = useCallback(async () => {
    if (selected.size === 0 || sending) return;
    setSending(true);
    setFeedback(null);
    const ids = Array.from(selected);
    const results = await Promise.allSettled(
      ids.map((routeId) =>
        client.rpc('publish_route', { p_route_id: routeId }).then((res) => {
          if (res.error) throw res.error;
          return routeId;
        }),
      ),
    );
    const successes: string[] = [];
    const failures: string[] = [];
    for (let i = 0; i < results.length; i++) {
      const r = results[i];
      const id = ids[i];
      if (!id) continue;
      if (r && r.status === 'fulfilled') successes.push(id);
      else {
        failures.push(id);
        if (r && r.status === 'rejected') {
          Sentry.captureException(r.reason, { tags: { area: 'dispatch.send_routes' } });
        }
      }
    }
    setSelected((prev) => {
      const next = new Set(prev);
      for (const id of successes) next.delete(id);
      return next;
    });

    // Push notify each newly-published route's assigned driver.
    // Fire-and-forget in parallel — dispatcher toast doesn't wait.
    if (successes.length > 0) {
      void (async () => {
        try {
          const { data: sess } = await client.auth.getSession();
          const token = sess.session?.access_token;
          if (!token) return;
          await Promise.allSettled(
            successes.map((routeId) =>
              fetch('/api/notifications/route-published', {
                method: 'POST',
                headers: {
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({ route_id: routeId }),
              }),
            ),
          );
        } catch (err) {
          Sentry.captureException(err, {
            tags: { area: 'dispatch.send_routes.notify' },
          });
        }
      })();
    }

    setSending(false);
    if (failures.length === 0) {
      setFeedback({
        tone: 'success',
        text: `Sent ${successes.length} ${successes.length === 1 ? 'route' : 'routes'}.`,
      });
    } else if (successes.length === 0) {
      setFeedback({
        tone: 'error',
        text: `Could not send ${failures.length} ${failures.length === 1 ? 'route' : 'routes'}. Try again or open each to see the reason.`,
      });
    } else {
      setFeedback({
        tone: 'error',
        text: `Sent ${successes.length}, failed ${failures.length}. Open the failed cards to retry individually.`,
      });
    }
    await fetchDay(date);
  }, [client, date, fetchDay, selected, sending]);

  const loading = rows === null && !error;
  const empty = rows !== null && rows.length === 0 && !error;
  const totalDrivers = allDrivers?.length ?? 0;
  const activeDrivers = totals.activeDriverIds.size;

  // Map view collapses the top chrome onto a single row so the map
  // itself fills essentially the whole content viewport — matches
  // CNC's dispatch layout where the map is the primary surface.
  const hasContent = !loading && groups.length > 0;
  const isMapView = view === 'map' && hasContent;

  return (
    <div className={isMapView ? 'space-y-2' : 'space-y-3'}>
      {isMapView ? (
        // Compact single-row header: DayStepper + Toggle + Send Routes.
        // Skips PageHeadline entirely — day is visible in the stepper,
        // stats are in the map's HUD, Send Routes lives inline.
        <div className="flex flex-wrap items-center gap-3">
          <DayStepper date={date} onChange={onDateChange} />
          <div className="ml-auto flex items-center gap-2">
            <ViewToggle view={view} onChange={setView} />
            <SendRoutesButton
              selectedCount={selected.size}
              sending={sending}
              onSendRoutes={sendRoutes}
            />
          </div>
        </div>
      ) : (
        <>
          <DayStepper date={date} onChange={onDateChange} />
          <PageHeadline
            date={date}
            selectedCount={selected.size}
            sending={sending}
            onSendRoutes={sendRoutes}
          />
          <StatTrio
            totalStops={totals.totalStops}
            coldChain={totals.coldChain}
            activeDrivers={activeDrivers}
            totalDrivers={totalDrivers}
            loading={loading}
          />
        </>
      )}

      {feedback && <Alert tone={feedback.tone}>{feedback.text}</Alert>}

      {error && <Alert tone="error">{error}</Alert>}

      {loading && (
        <Card className="p-6 text-sm text-ink-3">Loading today’s workload…</Card>
      )}

      {empty && (
        <Card className="p-6">
          <h2 className="text-base font-semibold text-ink">No routes for {longDayLabel(date)}</h2>
          <p className="mt-1 text-sm text-ink-2">
            When pharmacies upload orders and a draft route is created, it
            will appear here grouped by pharmacy.
          </p>
        </Card>
      )}

      {hasContent && (
        <>
          {view === 'stops' && <ViewToggle view={view} onChange={setView} />}
          {view === 'stops' ? (
            <div className="space-y-6">
              {groups.map((group) => (
                <PharmacyCohort
                  key={group.pharmacyId}
                  group={group}
                  selected={selected}
                  onToggle={toggleSelected}
                />
              ))}
            </div>
          ) : (
            // Edge-to-edge map, no Card chrome. Height accounts for
            // the compact top row (~4rem incl. shell padding + gap)
            // + a small bottom breathing room. Floor at 500px.
            <div
              className="overflow-hidden rounded-lg ring-1 ring-border"
              style={{ height: 'calc(100vh - 6rem)', minHeight: 500 }}
            >
              <DispatchMap stops={mapStops} />
            </div>
          )}
        </>
      )}

      {/* NoStopsFooter is a Stops-view concern (who's idle today) —
          the map view doesn't need it (drivers with no work don't
          appear on the map). */}
      {!loading && view === 'stops' && noStopsDrivers.length > 0 && (
        <NoStopsFooter drivers={noStopsDrivers} />
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Day-of-week stepper (Mon..Fri of the workweek containing `date`).

function DayStepper({
  date,
  onChange,
}: {
  date: string;
  onChange: (next: string) => void;
}): JSX.Element {
  const week = useMemo(() => workweekOf(date), [date]);
  const start = week[0] ?? date;
  const end = week[week.length - 1] ?? date;
  return (
    <div className="flex items-center gap-3">
      <div className="inline-flex items-center rounded-lg border border-border bg-bg p-1 shadow-card">
        {week.map((d) => {
          const active = d === date;
          return (
            <button
              key={d}
              type="button"
              onClick={() => onChange(d)}
              className={
                'rounded-md px-3 py-1.5 text-sm font-medium transition ' +
                (active
                  ? 'bg-ink text-white'
                  : 'text-ink-2 hover:bg-bg-hover hover:text-ink')
              }
              aria-pressed={active}
            >
              {shortDayLabel(d)}
            </button>
          );
        })}
      </div>
      <span className="text-sm text-ink-3">{rangeLabel(start, end)}</span>
    </div>
  );
}

// ----------------------------------------------------------------------------
// "{Day} Delivery" headline row with Send Routes action.

function PageHeadline({
  date,
  selectedCount,
  sending,
  onSendRoutes,
}: {
  date: string;
  selectedCount: number;
  sending: boolean;
  onSendRoutes: () => void;
}): JSX.Element {
  const dayLabel = longDayLabel(date);
  return (
    <div className="flex items-end justify-between">
      <div>
        <h1 className="text-xl font-semibold text-ink">{dayLabel} Delivery</h1>
        <div className="mt-0.5 text-xs text-ink-3">{fullDateLabel(date)}</div>
      </div>
      <div className="flex items-center gap-2">
        <SendRoutesButton
          selectedCount={selectedCount}
          sending={sending}
          onSendRoutes={onSendRoutes}
        />
      </div>
    </div>
  );
}

// Standalone Send Routes button — extracted from PageHeadline so the
// compact map-view header can render it inline with DayStepper +
// ViewToggle without pulling in the "{Day} Delivery" heading.
function SendRoutesButton({
  selectedCount,
  sending,
  onSendRoutes,
}: {
  selectedCount: number;
  sending: boolean;
  onSendRoutes: () => void;
}): JSX.Element {
  const sendDisabled = selectedCount === 0 || sending;
  return (
    <button
      type="button"
      onClick={onSendRoutes}
      disabled={sendDisabled}
      className={
        'rounded-md px-3 py-1.5 text-sm font-semibold transition ' +
        (sendDisabled
          ? 'cursor-not-allowed bg-bg-soft text-ink-3'
          : 'bg-ink text-white hover:bg-ink/90')
      }
    >
      {sending ? 'Sending…' : selectedCount > 0 ? `Send Routes (${selectedCount})` : 'Send Routes'}
    </button>
  );
}

// ----------------------------------------------------------------------------
// Stops | Map view toggle. Segmented control mirroring DayStepper's
// visual language. Placed above the pharmacy cohorts so the day's
// workload can be inspected as either the grouped list (default) or
// the CNC-style dispatch map.

function ViewToggle({
  view,
  onChange,
}: {
  view: 'stops' | 'map';
  onChange: (next: 'stops' | 'map') => void;
}): JSX.Element {
  const opts: Array<{ key: 'stops' | 'map'; label: string }> = [
    { key: 'stops', label: 'Stops' },
    { key: 'map', label: 'Map' },
  ];
  return (
    <div className="flex justify-end">
      <div
        role="tablist"
        aria-label="Dispatch view"
        className="inline-flex items-center rounded-lg border border-border bg-bg p-1 shadow-card"
      >
        {opts.map((o) => {
          const active = view === o.key;
          return (
            <button
              key={o.key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onChange(o.key)}
              className={
                'rounded-md px-3 py-1.5 text-sm font-medium transition ' +
                (active
                  ? 'bg-ink text-white'
                  : 'text-ink-2 hover:bg-bg-hover hover:text-ink')
              }
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Big-number stat trio.

function StatTrio({
  totalStops,
  coldChain,
  activeDrivers,
  totalDrivers,
  loading,
}: {
  totalStops: number;
  coldChain: number;
  activeDrivers: number;
  totalDrivers: number;
  loading: boolean;
}): JSX.Element {
  const dash = '—';
  const stopsText = loading ? dash : String(totalStops);
  const coldText = loading ? dash : String(coldChain);
  const driversText = loading
    ? dash
    : totalDrivers > 0
    ? `${activeDrivers}/${totalDrivers}`
    : String(activeDrivers);
  // CNC's stats are inline text, no card chrome. Same treatment in
  // /schedule.
  return (
    <div className="grid grid-cols-3 gap-x-6 gap-y-2 px-1">
      <div className="flex flex-col items-center text-center">
        <div className="text-2xl font-semibold leading-none text-ink">{stopsText}</div>
        <div className="mt-1 text-[10px] uppercase tracking-wide text-ink-3">Total stops</div>
      </div>
      <div className="flex flex-col items-center text-center">
        <div
          className={
            'text-2xl font-semibold leading-none ' +
            (!loading && coldChain > 0 ? 'text-signal-blue' : 'text-ink')
          }
        >
          {coldText}
        </div>
        <div
          className={
            'mt-1 text-[10px] uppercase tracking-wide ' +
            (!loading && coldChain > 0 ? 'text-signal-blue' : 'text-ink-3')
          }
        >
          Cold chain
        </div>
      </div>
      <div className="flex flex-col items-center text-center">
        <div className="text-2xl font-semibold leading-none text-ink">{driversText}</div>
        <div className="mt-1 text-[10px] uppercase tracking-wide text-ink-3">Active drivers</div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Pharmacy cohort section header + driver-card grid.

function PharmacyCohort({
  group,
  selected,
  onToggle,
}: {
  group: PharmacyGroup;
  selected: Set<string>;
  onToggle: (routeId: string) => void;
}): JSX.Element {
  return (
    <section>
      <header className="mb-2 flex items-center gap-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-ink-2">
          {group.shortCode}
        </h2>
        <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-signal-blue px-1.5 text-[11px] font-semibold text-white">
          {group.drivers.length}
        </span>
      </header>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {group.drivers.map((card) => (
          <DriverWorkloadCard
            key={card.key}
            card={card}
            colorToken={group.colorToken}
            shortCode={group.shortCode}
            checked={selected.has(card.routeId)}
            onToggle={() => onToggle(card.routeId)}
          />
        ))}
      </div>
    </section>
  );
}

// Pharmacy-chip color classes. Kept off the primary teal so the chip
// reads as identity, not navigation.
const CHIP_CLASSES: Record<PharmacyColor, string> = {
  emerald: 'bg-emerald-50 text-emerald-800 border border-emerald-200',
  sky: 'bg-blue-50 text-blue-800 border border-blue-200',
  amber: 'bg-amber-50 text-amber-800 border border-amber-200',
  violet: 'bg-violet-50 text-violet-800 border border-violet-200',
  slate: 'bg-slate-100 text-slate-700 border border-slate-200',
};

function DriverWorkloadCard({
  card,
  colorToken,
  shortCode,
  checked,
  onToggle,
}: {
  card: DriverCard;
  colorToken: PharmacyColor;
  shortCode: string;
  checked: boolean;
  onToggle: () => void;
}): JSX.Element {
  // Checkbox only renders on drafts — published / in-progress / completed
  // routes can't be re-published, so selection would be misleading.
  const selectable = card.routeStatus === 'draft';
  // Whole card is the click target for the route detail (added
  // 2026-07-10 — previously only the driver name was clickable, a tiny
  // hit surface on a large card). The Link sits as an absolute overlay
  // beneath the card content; the checkbox lives above via `relative
  // z-10` so its own onChange fires without bubbling into a route
  // navigation.
  return (
    <div className="group relative rounded-lg border border-border bg-bg p-4 shadow-card transition hover:border-ink-3 focus-within:border-ink-3">
      <Link
        to="/routes/$routeId"
        params={{ routeId: card.routeId }}
        aria-label={`Open route for ${card.driverLabel}`}
        className="absolute inset-0 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      />
      <div className="relative flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          {selectable && (
            <input
              type="checkbox"
              checked={checked}
              onChange={onToggle}
              onClick={(e) => e.stopPropagation()}
              aria-label={`Select route for ${card.driverLabel}`}
              className="relative z-10 h-4 w-4 cursor-pointer rounded border-border text-primary focus:ring-primary"
            />
          )}
          <span className="text-sm font-semibold text-ink group-hover:underline">
            {card.driverLabel}
          </span>
        </div>
        <span
          className={
            'inline-flex items-center rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ' +
            CHIP_CLASSES[colorToken]
          }
        >
          {shortCode}
        </span>
      </div>
      <div className="relative mt-3 flex items-end gap-5">
        <div>
          <div className="text-2xl font-semibold text-ink">{card.stopCount}</div>
          <div className="text-[10px] uppercase tracking-wide text-ink-3">Stops</div>
        </div>
        {card.coldChainCount > 0 && (
          <div>
            <div className="text-2xl font-semibold text-signal-blue">{card.coldChainCount}</div>
            <div className="text-[10px] uppercase tracking-wide text-signal-blue">Cold chain</div>
          </div>
        )}
        <div className="ml-auto">
          <StatusBadge status={card.routeStatus} entity="route" />
        </div>
      </div>
      <div className="relative">
        <RouteProgressStrip
          delivered={card.deliveredCount}
          total={card.stopCount}
          status={card.routeStatus}
        />
      </div>
    </div>
  );
}

// Inline progress for a driver-workload card. Only renders once a
// route has produced at least one delivered order (in_progress or
// completed) or is itself terminal — pre-flight drafts and assigned
// routes don't need a strip yet. The fill is green for forward
// progress; the trailing text reads "X/Y delivered".
function RouteProgressStrip({
  delivered,
  total,
  status,
}: {
  delivered: number;
  total: number;
  status: string;
}): JSX.Element | null {
  // Hide for routes that haven't started yet AND have nothing
  // delivered. Once a single stop is delivered we render even if the
  // route is somehow still in 'assigned' (defensive — Realtime updates
  // could land a delivered order before the route_status flips).
  const isMidOrLate =
    status === 'in_progress' || status === 'completed' || delivered > 0;
  if (!isMidOrLate) return null;
  if (total === 0) return null;
  const pct = Math.min(100, Math.round((delivered / total) * 100));
  return (
    <div className="mt-3">
      <div className="h-1 w-full overflow-hidden rounded-full bg-bg-soft">
        <div
          className="h-full rounded-full bg-signal-green transition-[width]"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="mt-1 text-[10px] text-ink-3">
        <span className="font-semibold text-ink-2">{delivered}</span>
        /{total} delivered
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// "NO STOPS TODAY (N)" footer — drivers in the tenant who don't have a
// route on the visible date. Helpful when a dispatcher is wondering "did
// I forget to assign someone?" The list is a flat, muted band.

function NoStopsFooter({
  drivers,
}: {
  drivers: { id: string; label: string }[];
}): JSX.Element {
  return (
    <section className="rounded-lg border border-dashed border-border bg-bg-soft px-4 py-3">
      <header className="mb-2 flex items-center gap-2">
        <h3 className="text-[11px] font-semibold uppercase tracking-wide text-ink-3">
          No stops today
        </h3>
        <span className="text-[11px] font-semibold text-ink-3">({drivers.length})</span>
      </header>
      <ul className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-ink-2">
        {drivers.map((d) => (
          <li key={d.id}>{d.label}</li>
        ))}
      </ul>
    </section>
  );
}
